import {
  ArrowRight,
  Building2,
  Home as HomeIcon,
  Landmark,
  Map,
  MapPin,
  Mountain,
  Search,
  Sparkles,
  Store,
  Sun,
  TreePine,
  TrendingUp,
  Waves,
  Wheat,
} from "lucide-react";
import React, { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import "swiper/css";
import "swiper/css/pagination";
import { getIcon } from "../../helpers/iconMap";
import { regionsHelper } from "../../helpers/regions";
import {
  useGetCategoryCounts,
  useGetPropertiesWithFilters,
  useGetRegionCounts,
} from "../../hooks/useListing";
import { useListingsParams } from "../../hooks/useListingParams";
import { useParallax } from "../../hooks/useParallax";
import { useListingStore } from "../../store/useListingStore";
import BannerTuraqKg from "../components/BannerTuraqKg";
import CategorySection from "../components/CategorySection";
import { ListingCard } from "../components/ListingCard";
import { ListingsSearch } from "../components/ListingsSearch";
import Partners from "../components/Partners";
// Bottom of the page and the only user of framer-motion here: loaded lazily so
// the ~40 KB animation library is not part of the first paint.
const SafetySection = lazy(() => import("../components/SafetySection"));
import { useDocumentMeta } from "../../hooks/useDocumentMeta";
import { proEnabled } from "../../pro/flags";
import { seoMeta } from "../data/seoMeta";
import { CATEGORIES, REGIONS } from "../data/mockData";
import {
  SEO_CATEGORIES,
  SEO_LANGS,
  SEO_REGIONS,
  hubPath,
  type LangCode,
} from "../data/regions";

// TURAQ Pro: new buildings block, loaded only with the flag on.
const HomeNewBuildings = lazy(() => import("../../pro/complexes/HomeBlock"));

const CATEGORY_ICONS: Record<string, typeof Building2> = {
  APARTMENT: Building2,
  HOUSE: HomeIcon,
  PLOT: Map,
  COMMERCIAL: Store,
};

function pluralizeRu(n: number, forms: [string, string, string]): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return forms[0];
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return forms[1];
  return forms[2];
}

/** Per-region visual identity for the "Real estate by city" grid — a distinct
 *  icon + accent colour per oblast so the section reads less uniform, especially
 *  on mobile where the cards are small. */
const REGION_CARD: Record<
  string,
  {
    icon: typeof MapPin;
    chip: string;
    chipText: string;
    glow: string;
    arrowHover: string;
  }
> = {
  CHUI: {
    icon: Building2,
    chip: "from-blue-50 to-indigo-50",
    chipText: "text-blue-600",
    glow: "bg-blue-400/15",
    arrowHover: "group-hover:text-blue-600",
  },
  OSH: {
    icon: Landmark,
    chip: "from-amber-50 to-orange-50",
    chipText: "text-amber-600",
    glow: "bg-amber-400/20",
    arrowHover: "group-hover:text-amber-600",
  },
  JALAL_ABAD: {
    icon: TreePine,
    chip: "from-emerald-50 to-teal-50",
    chipText: "text-emerald-600",
    glow: "bg-emerald-400/20",
    arrowHover: "group-hover:text-emerald-600",
  },
  YSSYK_KOL: {
    icon: Waves,
    chip: "from-sky-50 to-cyan-50",
    chipText: "text-sky-600",
    glow: "bg-sky-400/20",
    arrowHover: "group-hover:text-sky-600",
  },
  NARYN: {
    icon: Mountain,
    chip: "from-violet-50 to-purple-50",
    chipText: "text-violet-600",
    glow: "bg-violet-400/20",
    arrowHover: "group-hover:text-violet-600",
  },
  TALAS: {
    icon: Wheat,
    chip: "from-lime-50 to-green-50",
    chipText: "text-lime-600",
    glow: "bg-lime-400/20",
    arrowHover: "group-hover:text-lime-600",
  },
  BATKEN: {
    icon: Sun,
    chip: "from-orange-50 to-red-50",
    chipText: "text-orange-600",
    glow: "bg-orange-400/20",
    arrowHover: "group-hover:text-orange-600",
  },
  DEFAULT: {
    icon: MapPin,
    chip: "from-blue-50 to-indigo-50",
    chipText: "text-blue-600",
    glow: "bg-blue-400/15",
    arrowHover: "group-hover:text-blue-600",
  },
};

export function Home() {
  const { t, i18n } = useTranslation();
  const homeMeta = seoMeta("home", i18n.language);
  useDocumentMeta({ title: homeMeta.title, description: homeMeta.description, canonical: "https://turaq.kg/" });
  const lang = (SEO_LANGS.find((l) => l.code === i18n.language)?.code ??
    "ru") as LangCode;
  const langPrefix =
    SEO_LANGS.find((l) => l.code === lang)?.urlPrefix || undefined;
  const [searchQuery, setSearchQuery] = useState("");
  const [params, updateParams] = useListingsParams();
  const [selectedRegion, setSelectedRegion] = useState("");
  const navigate = useNavigate();
  const { offset, isIOS } = useParallax();

  useEffect(() => {
    if (params.q) setSearchQuery(params.q);
    if (params.region) setSelectedRegion(params.region);
  }, [params.q, params.region]);

  useEffect(() => {
    if (searchQuery) updateParams({ q: searchQuery });
    if (selectedRegion) updateParams({ region: selectedRegion });
  }, [searchQuery, selectedRegion]);
  const getProperties = useGetPropertiesWithFilters();
  const { properties: recentListings } = useListingStore((state) => state);
  const [isFavoriteChanged, setIsFavoriteChanged] = useState<boolean>(false);
  const { counts: categoryCounts } = useGetCategoryCounts(
    CATEGORIES.map((c) => c.value),
  );
  const { counts: regionCounts } = useGetRegionCounts(
    SEO_REGIONS.map((r) => r.value),
  );
  const visibleRegions = SEO_REGIONS.filter((r) => regionCounts[r.value] !== 0);

  useEffect(() => {
    getProperties.mutate({
      sortBy: "createdAt",
      search: params.q,
      // A few spare so the grid stays full after hidden / blocked listings drop out.
      offset: 12,
      page: 0,
    });
    setIsFavoriteChanged(false);
  }, [isFavoriteChanged]);

  const handleSearch = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      // The field's own value, not `searchQuery`: that one trails the input
      // by a 350 ms debounce, so a quick tap on «Издөө» (or a slow phone
      // still booting) sent «?q=» with the typed text lost.
      const field = e.currentTarget.querySelector<HTMLInputElement>('input[type="search"]');
      const q = (field?.value ?? searchQuery).trim();
      if (!q && selectedRegion) {
        const backendValue = regionsHelper[selectedRegion];
        const hub = SEO_REGIONS.find((r) => r.value === backendValue);
        if (hub) {
          navigate(hubPath(hub.slug));
          return;
        }
      }
      const qs = new URLSearchParams();
      if (q) qs.set("q", q);
      if (selectedRegion) qs.set("region", selectedRegion);
      navigate(`/listings${qs.toString() ? `?${qs}` : ""}`);
    },
    [searchQuery, selectedRegion],
  );

  // const safetyIcons = useMemo(
  //   () => [ShieldAlert, CreditCard, UserCheck, PhoneOff, AlertTriangle, Lock],
  //   [],
  // );

  return (
    <div className="font-sans">
      {/* HERO */}
      <section
        className="hero-section relative text-white min-h-[calc(100svh-4rem-env(safe-area-inset-top))] sm:min-h-screen flex items-center"
        style={{
          backgroundSize: "cover",
          backgroundPosition: isIOS ? `center ${offset * 0.1}px` : "center",
          backgroundAttachment: isIOS ? "scroll" : "fixed",
        }}
      >
        {/* Gradient overlay — darker at top, fades out */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/10" />

        {/* Bottom fade для плавного перехода в следующую секцию */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white/10 to-transparent" />

        {/* Centred in the visible part of the screen: below the sticky header
            (min-h is the screen minus the header) and above the floating bottom
            nav on phones (extra bottom padding). min-h, not h, so taller content
            grows the section instead of spilling under the header. */}
        <div className="relative container mx-auto px-4 pt-8 pb-28 sm:py-24 md:py-36 w-full">
          <div className="max-w-2xl lg:max-w-3xl mx-auto text-center space-y-5">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-xs sm:text-sm font-medium text-white/90">
              <Sparkles className="w-3.5 h-3.5 text-blue-300" />
              {t("home.eyebrow")}
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-6xl font-extrabold leading-tight tracking-tight drop-shadow-lg break-words">
              {t("home.welcome")}
            </h1>

            <p className="text-base md:text-lg text-white/75 max-w-lg mx-auto leading-relaxed">
              {t("home.description")}
            </p>

            {/* Search card */}
            <form
              onSubmit={handleSearch}
              className="mt-8 bg-white rounded-2xl shadow-2xl text-left"
            >
              <div className="flex flex-col md:flex-row">
                {/* Search input */}
                <div className="flex-1 relative border-b md:border-b-0 md:border-r border-gray-100">
                  {/* <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /> */}
                  <ListingsSearch
                    placeholder={t("home.searchPlaceholder")}
                    className="h-14 w-full pl-11 pr-10 text-gray-900 text-base md:text-[15px] placeholder:text-gray-400 focus:outline-none focus-visible:ring-0 border-none rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none bg-transparent"
                    ariaLabel={t("home.searchPlaceholder")}
                    withSuggestions
                    withChrome
                    onSubmitQuery={(q) => {
                      const qs = new URLSearchParams();
                      if (q.trim()) qs.set("q", q.trim());
                      if (selectedRegion) qs.set("region", selectedRegion);
                      navigate(`/listings${qs.toString() ? `?${qs}` : ""}`);
                    }}
                  />
                </div>

                {/* Region select */}
                <div className="relative md:w-52 border-b md:border-b-0 md:border-r border-gray-100">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <select
                    value={selectedRegion}
                    onChange={(e) => setSelectedRegion(e.target.value)}
                    aria-label={t("filters.region", "Регион")}
                    className="border-none w-full h-14 pl-11 pr-4 text-base md:text-[15px] text-gray-700 focus:outline-none focus-visible:ring-0 rounded-xl appearance-none bg-transparent cursor-pointer"
                  >
                    <option value="">{t("filters.allRegions")}</option>
                    {REGIONS.map((region) => (
                      <option key={region.id} value={region.id}>
                        {t(`regions.${region.id}`)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  className="h-14 px-8 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-[15px] font-semibold transition-colors flex items-center justify-center gap-2 shrink-0 rounded-b-2xl md:rounded-bl-none md:rounded-r-2xl"
                >
                  <Search className="w-4 h-4" />
                  {t("buttons.submit")}
                </button>
              </div>
            </form>

            {/* Quick category shortcuts — fills the wide desktop hero with
                real navigation instead of empty background, and gets users
                straight to a hub page in one click. */}
            <div className="hidden sm:flex flex-wrap items-center justify-center gap-2 sm:gap-2.5 pt-1">
              {SEO_CATEGORIES.map((c) => {
                const Icon = CATEGORY_ICONS[c.value] ?? Building2;
                return (
                  <Link
                    key={c.slug}
                    to={hubPath(c.slug, undefined, undefined, langPrefix)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-xs sm:text-sm font-medium text-white/90 hover:text-white transition-all active:scale-95"
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {c.namesPlural[lang]}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* RECENT LISTINGS */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-slate-50/60 to-white py-12 md:py-20">
        <div
          className="pointer-events-none absolute right-0 top-0 h-56 w-56 opacity-[0.35]"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgba(37,99,235,0.35) 1px, transparent 1.5px)",
            backgroundSize: "16px 16px",
            maskImage:
              "radial-gradient(circle at top right, black, transparent 75%)",
            WebkitMaskImage:
              "radial-gradient(circle at top right, black, transparent 75%)",
          }}
        />

        <div className="relative container mx-auto px-4">
          <div className="flex flex-row items-center justify-between gap-3 mb-8 md:mb-10">
            <div>
              <div className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-xs font-semibold text-blue-600 mb-3">
                <TrendingUp className="w-3.5 h-3.5" />
                {t("home.recentEyebrow", "Новое на платформе")}
              </div>
              <h2 className="text-xl sm:text-3xl md:text-4xl font-black tracking-tight text-gray-900">
                {t("home.recentListings")}
              </h2>
            </div>
            <Link
              to="/listings"
              className="group inline-flex items-center gap-2 shrink-0 px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-full bg-white border border-gray-200 shadow-sm text-xs sm:text-sm font-semibold text-gray-700 hover:border-blue-300 hover:text-blue-600 hover:shadow-md transition-all"
            >
              {t("home.viewAll")}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {/* "Новое на платформе" = newest first (the API floats old TOP posts up). */}
            {[...recentListings]
              .sort(
                (a: any, b: any) =>
                  (Date.parse(b.createdat ?? b.createdAt) || 0) -
                  (Date.parse(a.createdat ?? a.createdAt) || 0),
              )
              .slice(0, 8)
              .map((listing) => (
                <ListingCard
                  key={listing.id}
                  listing={listing}
                  compact
                  setIsFavoriteChanged={() => setIsFavoriteChanged(true)}
                />
              ))}
          </div>
        </div>
      </section>
      <section>
        <BannerTuraqKg />
      </section>

      <section>
        <CategorySection
          categories={CATEGORIES}
          getIcon={getIcon}
          counts={categoryCounts}
        />
      </section>

      {proEnabled && (
        <Suspense fallback={null}>
          <HomeNewBuildings />
        </Suspense>
      )}

      {/* REAL ESTATE BY CITY */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#f4f7fd] via-[#f7f9fd] to-white py-14 md:py-24">
        {/* Bishkek photo panel, faded into the section background — same
            shot as the page hero, but a duotone crop confined to the left
            edge so it reads as a distinct decorative treatment rather than
            a repeat of the hero banner above. */}
        <div
          className="pointer-events-none absolute inset-y-0 left-0 hidden w-[42%] lg:block"
          style={{
            maskImage: "linear-gradient(to right, black 45%, transparent 92%)",
            WebkitMaskImage:
              "linear-gradient(to right, black 45%, transparent 92%)",
          }}
        >
          <img
            src="/hero/bishkek-1280.webp"
            loading="lazy"
            decoding="async"
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/50 via-blue-800/25 to-transparent mix-blend-multiply" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#f4f7fd] via-transparent to-[#f4f7fd]/40" />
        </div>

        {/* Dot-grid texture in the corners — echoes the reference design's
            paper-like background instead of a flat white section. */}
        <div
          className="pointer-events-none absolute left-0 top-0 h-40 w-40 sm:h-56 sm:w-56 opacity-[0.35]"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgba(37,99,235,0.35) 1px, transparent 1.5px)",
            backgroundSize: "16px 16px",
            maskImage:
              "radial-gradient(circle at top left, black, transparent 75%)",
            WebkitMaskImage:
              "radial-gradient(circle at top left, black, transparent 75%)",
          }}
        />
        <div
          className="pointer-events-none absolute right-0 bottom-0 h-40 w-40 sm:h-56 sm:w-56 opacity-[0.35]"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgba(79,70,229,0.35) 1px, transparent 1.5px)",
            backgroundSize: "16px 16px",
            maskImage:
              "radial-gradient(circle at bottom right, black, transparent 75%)",
            WebkitMaskImage:
              "radial-gradient(circle at bottom right, black, transparent 75%)",
          }}
        />

        {/* Soft color blobs */}
        <div className="pointer-events-none absolute -top-24 -left-24 h-64 w-64 rounded-full bg-blue-100/60 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-indigo-100/50 blur-3xl" />

        {/* Big decorative pin, echoing the reference's oversized map marker */}
        <MapPin
          className="pointer-events-none absolute right-[6%] top-10 hidden h-28 w-28 -rotate-6 text-blue-600/10 md:block lg:h-36 lg:w-36"
          strokeWidth={1.5}
          fill="currentColor"
        />

        <div className="relative container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-xs font-semibold text-blue-600 mb-4">
              <MapPin className="w-3.5 h-3.5" />
              {t("home.citiesEyebrow", "7 областей и Бишкек")}
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight leading-[1.1]">
              <span className="block text-gray-900">
                {t("home.citiesTitleLine1", "Недвижимость")}
              </span>
              <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {t("home.citiesTitleLine2", "по городам")}
              </span>
            </h2>
            <p className="mt-3 text-sm sm:text-base text-gray-500">
              {t("home.citiesSubtitle")}
            </p>
          </div>

          <div
            className={`grid grid-cols-2 gap-3 sm:gap-4 lg:gap-5 ${
              // Fewer cities than columns left an empty hole on the right — fill the row instead.
              visibleRegions.length <= 3 ? "sm:grid-cols-3" : "sm:grid-cols-3 lg:grid-cols-4"
            }`}
          >
            {/* Regions without listings are hidden until someone posts there. */}
            {visibleRegions.map(
              (region) => {
                const isCapital = region.value === "BISHKEK";
                const cfg = REGION_CARD[region.value] ?? REGION_CARD.DEFAULT;
                const RegionIcon = isCapital ? Building2 : cfg.icon;
                const to = hubPath(
                  region.slug,
                  undefined,
                  undefined,
                  langPrefix,
                );
                const viewHint = t(
                  "home.citiesViewHint",
                  "Смотреть объявления",
                );
                const regionCount = regionCounts[region.value];
                const isRu = i18n.language?.startsWith("ru");
                const countLabel =
                  regionCount === undefined
                    ? null
                    : isRu
                      ? `${regionCount} ${pluralizeRu(regionCount, ["объект", "объекта", "объектов"])}`
                      : `${regionCount} ${t("home.citiesObjectsWord", { defaultValue: "объект" })}`;
                const hint = countLabel ?? viewHint;

                if (isCapital) {
                  return (
                    <Link
                      key={region.slug}
                      to={to}
                      className={`group relative col-span-2 ${visibleRegions.length <= 3 ? "sm:col-span-1" : "lg:col-span-1"} overflow-hidden rounded-2xl
                      bg-gradient-to-br from-blue-600 via-blue-600 to-indigo-700 p-5 lg:p-6
                      text-white shadow-lg shadow-blue-600/25 ring-1 ring-blue-500/40
                      transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-600/35
                      active:scale-[0.97] active:duration-75`}
                    >
                      <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/15 blur-2xl" />
                      <div
                        className="pointer-events-none absolute inset-0 opacity-20"
                        style={{
                          backgroundImage:
                            "radial-gradient(circle, rgba(255,255,255,0.6) 1px, transparent 1.5px)",
                          backgroundSize: "14px 14px",
                          maskImage:
                            "radial-gradient(circle at 92% 8%, black, transparent 70%)",
                          WebkitMaskImage:
                            "radial-gradient(circle at 92% 8%, black, transparent 70%)",
                        }}
                      />

                      <div className="relative flex items-start justify-between gap-3">
                        <div className="flex h-12 w-12 lg:h-14 lg:w-14 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25 backdrop-blur">
                          <RegionIcon className="h-6 w-6" />
                        </div>
                        <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide backdrop-blur">
                          {t("home.citiesCapitalBadge", "Столица")}
                        </span>
                      </div>

                      <div className="relative mt-4 flex items-end justify-between gap-2">
                        <div className="min-w-0">
                          <span className="block text-lg lg:text-xl font-black leading-tight">
                            {region.names[lang]}
                          </span>
                          <span className="mt-0.5 block text-xs text-white/70">
                            {hint}
                          </span>
                        </div>
                        <ArrowRight className="h-5 w-5 shrink-0 text-white/80 transition-transform duration-300 group-hover:translate-x-1" />
                      </div>
                    </Link>
                  );
                }

                return (
                  <Link
                    key={region.slug}
                    to={to}
                    className="group relative flex flex-col overflow-hidden rounded-2xl bg-white
                    p-4 sm:p-5 lg:p-6 shadow-sm ring-1 ring-gray-100
                    transition-all duration-300
                    hover:-translate-y-1 hover:shadow-lg hover:ring-blue-200
                    active:scale-[0.97] active:duration-75"
                  >
                    <div
                      className={`pointer-events-none absolute -right-6 -top-8 h-24 w-24 rounded-full blur-2xl opacity-70 transition-opacity duration-300 group-hover:opacity-100 ${cfg.glow}`}
                    />

                    <div className="relative flex items-center justify-between">
                      <div
                        className={`flex h-11 w-11 lg:h-14 lg:w-14 items-center justify-center rounded-xl
                        bg-gradient-to-br ${cfg.chip} ${cfg.chipText}
                        transition-transform duration-300 group-hover:scale-105`}
                      >
                        <RegionIcon className="h-5 w-5 lg:h-6 lg:w-6" />
                      </div>
                      <ArrowRight
                        className={`h-4 w-4 lg:h-5 lg:w-5 shrink-0 text-gray-300 transition-all duration-300 group-hover:translate-x-1 ${cfg.arrowHover}`}
                      />
                    </div>

                    <span className="relative mt-3 text-sm sm:text-base lg:text-lg font-bold leading-snug text-gray-900">
                      {region.names[lang]}
                    </span>
                    <span className="relative mt-0.5 text-[11px] sm:text-xs text-gray-400">
                      {hint}
                    </span>
                  </Link>
                );
              },
            )}
          </div>

          {/* Bottom tagline divider */}
          <div className="relative flex items-center justify-center mt-10 sm:mt-14">
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 border-t border-dashed border-blue-200" />
            <div className="relative flex items-center gap-3 bg-transparent px-4">
              <span className="hidden sm:flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-blue-100 text-blue-600 shadow-sm">
                <Building2 className="h-4 w-4" />
              </span>
              <p className="bg-gradient-to-b from-[#f4f7fd] via-[#f7f9fd] to-white px-2 text-xs sm:text-sm font-medium text-gray-400 text-center">
                {t(
                  "home.citiesTagline",
                  "Начните со своего города — удобный поиск, быстрый результат!",
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section>
        <Suspense fallback={<div className="min-h-[520px]" aria-hidden />}>
          <SafetySection />
        </Suspense>
      </section>

      {/* PARTNERS */}
      <section>
        <Partners />
      </section>
    </div>
  );
}
